import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Shipping state
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    country: '',
    postal_code: '',
    city: '',
    state: '',
    address1: ''
  });
  const [shippingCosts, setShippingCosts] = useState({}); // { itemId: cost }
  const [totalShippingCost, setTotalShippingCost] = useState(0);
  const [calculationTimestamp, setCalculationTimestamp] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('chesswebCart');
    const savedShipping = localStorage.getItem('chesswebShipping');

    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        localStorage.removeItem('chesswebCart');
      }
    }

    if (savedShipping) {
      try {
        const shipping = JSON.parse(savedShipping);
        setShippingCalculated(shipping.shippingCalculated || false);
        setShippingAddress(shipping.shippingAddress || {});
        setShippingCosts(shipping.shippingCosts || {});
        setTotalShippingCost(shipping.totalShippingCost || 0);
        setCalculationTimestamp(shipping.calculationTimestamp || null);
      } catch (error) {
        console.error('Error loading shipping from localStorage:', error);
        localStorage.removeItem('chesswebShipping');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('chesswebCart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Save shipping to localStorage whenever it changes
  useEffect(() => {
    const shippingData = {
      shippingCalculated,
      shippingAddress,
      shippingCosts,
      totalShippingCost,
      calculationTimestamp
    };
    localStorage.setItem('chesswebShipping', JSON.stringify(shippingData));
  }, [shippingCalculated, shippingAddress, shippingCosts, totalShippingCost, calculationTimestamp]);

  // Clear shipping when cart changes
  const clearShipping = () => {
    setShippingCalculated(false);
    setShippingCosts({});
    setTotalShippingCost(0);
    setCalculationTimestamp(null);
  };

  // Add item to cart
  const addToCart = (item) => {
    // item should have: productId, variantId, sizeColorKey, selectedSize, selectedColor, quantity, snapshot
    setCartItems((prevItems) => {
      // Check if item already exists (same product + variant + size/color combination)
      const existingItemIndex = prevItems.findIndex(
        (i) =>
          i.productId === item.productId &&
          i.variantId === item.variantId &&
          i.sizeColorKey === item.sizeColorKey
      );

      if (existingItemIndex > -1) {
        // Item exists, update quantity
        const newItems = [...prevItems];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + (item.quantity || 1)
        };
        clearShipping(); // Clear shipping when cart changes
        return newItems;
      } else {
        // New item, add to cart
        const newItem = {
          ...item,
          id: `${item.productId}_${item.variantId || 'default'}_${item.sizeColorKey || 'default'}_${Date.now()}`,
          quantity: item.quantity || 1
        };
        clearShipping(); // Clear shipping when cart changes
        return [...prevItems, newItem];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (itemId) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
    clearShipping(); // Clear shipping when cart changes
  };

  // Update item quantity
  const updateQuantity = (itemId, quantity) => {
    if (quantity < 1) {
      removeFromCart(itemId);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, quantity } : item
      )
    );
    clearShipping(); // Clear shipping when cart changes
  };

  // Clear entire cart
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // Get cart totals
  const getCartTotals = () => {
    const subtotal = cartItems.reduce((total, item) => {
      return total + (item.snapshot.price * item.quantity);
    }, 0);

    const itemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

    // Determine if cart has POD items (for shipping calculation)
    const hasPODItems = cartItems.some((item) => item.snapshot.product_type === 'pod');

    return {
      subtotal,
      itemCount,
      hasPODItems
    };
  };

  // Toggle cart sidebar
  const toggleCart = () => {
    setIsCartOpen((prev) => !prev);
  };

  const openCart = () => {
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  // Set shipping calculation results
  const setShippingCalculation = (address, costs, total) => {
    setShippingAddress(address);
    setShippingCosts(costs);
    setTotalShippingCost(total);
    setShippingCalculated(true);
    setCalculationTimestamp(Date.now());
  };

  // Update shipping address (clears calculation if changed)
  const updateShippingAddress = (newAddress) => {
    // If address changed after calculation, clear it
    if (shippingCalculated) {
      clearShipping();
    }
    setShippingAddress(newAddress);
  };

  const value = {
    cartItems,
    isCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotals,
    toggleCart,
    openCart,
    closeCart,
    // Shipping
    shippingCalculated,
    shippingAddress,
    shippingCosts,
    totalShippingCost,
    calculationTimestamp,
    setShippingCalculation,
    updateShippingAddress,
    clearShipping
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
